/* ----------------------------------------------------------------------------
 * useRecommendVoice — conversational push/publish/write glue (owner GO 12-10-2026
 * 20:30 UTC "go", spec verbatim 20:29 UTC in docs/brain/governance/owner-decision-
 * aura-publish-choice-2026-10-10.md). "Hey Aura, what do you recommend?" ->
 * ONE numbered list from what already exists (ready drafts first = publish, then
 * SEO-Opportunities topics = write), the top 3 spoken briefly, then "Shall I publish
 * and write them all now, or tell me the number or name?" -- the capture stays open.
 *
 * It EXECUTES NOTHING itself. A selection is handed to the existing flows:
 *   - publish picks -> useBatchPublishVoice.presentDrafts(mode 'publish'): the P1
 *     read-back naming action + site + count, pure-yes/tap confirm, re-push guard,
 *     per-site rules (/partners draft_only) and owner/operator gate all unchanged;
 *   - write picks  -> useSuggestWriteVoice.adoptAndWrite: the existing runWrites
 *     (server cap check, limit warning, owner/operator, P1 hand-off of the drafts).
 * A mixed selection publishes first; the writes run only after the publish confirm
 * executed (a "no" to the publish read-back writes nothing).
 * Errors are never swallowed: each lands in view.error and is spoken briefly.
 * -------------------------------------------------------------------------- */

import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import type { ConversationTurn } from '../wake/turnEntity.ts';
import {
  BATCH_MAX_COUNT,
  BATCH_NO_SITE,
  BATCH_PICK_SITE,
  BATCH_TARGET_UNLINKED_REASON,
  batchTargetUnlinkedLine,
  isPublishFeatureForbidden,
  matchDraftTitle,
  resolveBatchSite,
  type BatchActiveDomain,
  type BatchChannel,
  type BatchMode,
  type BatchPublishAsk,
  type BatchSiteOption,
} from './batchPublishModel.ts';
import type { BatchPublishDeps } from './useBatchPublishVoice.ts';
import type { SuggestResult, SuggestWriteDeps } from './useSuggestWriteVoice.ts';
import type { SuggestWriteAsk } from './suggestWriteModel.ts';
import {
  RECOMMEND_CANCEL_ACK,
  RECOMMEND_MAX_REASKS,
  RECOMMEND_NONE,
  RECOMMEND_OWNER_OPERATOR_ONLY,
  RECOMMEND_WHICH,
  buildRecommendList,
  clarifyLine,
  parseListSelection,
  recommendSpokenLine,
  type RecommendAsk,
  type RecommendItem,
} from './recommendModel.ts';

export interface RecommendView {
  phase: 'loading' | 'choose_site' | 'asking' | 'none' | 'handed_off' | 'cancelled' | 'error' | 'forbidden';
  channel: BatchChannel;
  source: RecommendAsk['source'];
  scope: RecommendAsk['scope'];
  sites: BatchSiteOption[];
  site: BatchSiteOption | null;
  items: RecommendItem[];
  line: string;
  /** why the write items cannot be written right now (AI limit / store off), shown on screen. */
  writeNote: string;
  error: string;
}

export interface UseRecommendVoiceArgs {
  batchDeps: Pick<BatchPublishDeps, 'listSites' | 'candidates'>;
  suggestDeps: Pick<SuggestWriteDeps, 'suggest'>;
  speak: (text: string) => Promise<void>;
  turnRef: MutableRefObject<ConversationTurn>;
  listenRef: MutableRefObject<(() => Promise<unknown>) | null>;
  activeDomain?: () => BatchActiveDomain | null;
  /** owner/operator only (owner decisions 10-10-2026 16:33 UTC + 12-10-2026 15:39 UTC). Absent = NO. */
  canManage?: () => boolean;
  batch: {
    begin: (ask: BatchPublishAsk, channel: BatchChannel) => Promise<void>;
    presentDrafts: (site: BatchSiteOption, draftIds: number[], channel: BatchChannel, opts?: { mode?: BatchMode | null; afterConfirm?: () => Promise<void> }) => Promise<void>;
  };
  suggest: {
    begin: (ask: SuggestWriteAsk, channel: BatchChannel) => Promise<void>;
    adoptAndWrite: (r: SuggestResult, site: BatchSiteOption, indexes: number[], channel: BatchChannel) => Promise<void>;
  };
}

export interface RecommendVoice {
  view: RecommendView | null;
  begin: (ask: RecommendAsk, channel: BatchChannel) => Promise<void>;
  handleAnswer: (answer: string, channel: BatchChannel) => Promise<void>;
  /** "write <title>" routing: a queued (already written) draft -> the publish offer; else the write flow. */
  beginWrite: (ask: SuggestWriteAsk, channel: BatchChannel) => Promise<void>;
  pick: (n: number) => Promise<void>;
  pickAll: () => Promise<void>;
  chooseSite: (siteId: number) => Promise<void>;
  dismiss: () => void;
}

function errText(e: unknown): string {
  const code = (e as { code?: string; body?: { error?: { code?: string } } })?.body?.error?.code ?? (e as { code?: string })?.code;
  return code ? 'That didn’t go through (' + String(code).replace(/_/g, ' ') + ').' : 'That didn’t go through. Please try again.';
}

/** Words that make a keyword look like a TITLE of an existing draft (>= 4 content words). */
function titleLike(k: string): boolean {
  return k.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2 && !['the', 'and', 'for', 'about', 'with', 'your', 'from'].includes(w)).length >= 4;
}

export function useRecommendVoice(args: UseRecommendVoiceArgs): RecommendVoice {
  const { speak, turnRef, listenRef } = args;
  const [view, setView] = useState<RecommendView | null>(null);
  const viewRef = useRef<RecommendView | null>(null);
  const suggestionRef = useRef<SuggestResult | null>(null);
  const set = useCallback((next: RecommendView | null) => { viewRef.current = next; setView(next); }, []);
  const patch = useCallback((p: Partial<RecommendView>) => { if (viewRef.current) set({ ...viewRef.current, ...p }); }, [set]);
  const say = useCallback(async (line: string) => {
    try { await speak(line); } catch (e) { patch({ error: 'Aura could not speak just now — the list is on screen.' }); void e; }
  }, [speak, patch]);
  const allowed = useCallback(() => (args.canManage ? args.canManage() : false), [args]);
  const listen = useCallback(async (channel: BatchChannel) => {
    if (channel === 'voice' && turnRef.current.subState?.kind === 'recommend') await listenRef.current?.();
  }, [turnRef, listenRef]);

  const load = useCallback(async (site: BatchSiteOption, ask: RecommendAsk, channel: BatchChannel) => {
    suggestionRef.current = null;
    patch({ phase: 'loading', site, sites: [], line: 'Looking at what’s ready for ' + site.name + '…' });
    const [dr, tr] = await Promise.allSettled([
      ask.scope !== 'write' ? args.batchDeps.candidates(site.id, BATCH_MAX_COUNT) : Promise.resolve(null),
      ask.scope !== 'publish' ? args.suggestDeps.suggest(site.id, channel, site.projectId) : Promise.resolve(null),
    ]);
    if (dr.status === 'rejected' && isPublishFeatureForbidden(dr.reason)) {
      turnRef.current.clearSubStateIfKind('recommend');
      set({ ...(viewRef.current as RecommendView), phase: 'forbidden', items: [], line: RECOMMEND_OWNER_OPERATOR_ONLY });
      await say(RECOMMEND_OWNER_OPERATOR_ONLY);
      return;
    }
    const drafts = dr.status === 'fulfilled' && dr.value ? dr.value.items ?? [] : [];
    const sugg = tr.status === 'fulfilled' ? tr.value : null;
    suggestionRef.current = sugg;
    const topics = sugg?.items ?? [];
    const items = buildRecommendList(drafts, topics);
    const failed = [dr, tr].filter((r) => r.status === 'rejected').map((r) => errText((r as PromiseRejectedResult).reason));
    const writeNote = sugg && topics.length > 0 && !(sugg.write?.available && sugg.confirm_token) ? (sugg.write?.message || 'Writing isn’t available right now.') : '';
    if (items.length === 0) {
      const unlinked = dr.status === 'fulfilled' && dr.value?.reason === BATCH_TARGET_UNLINKED_REASON;
      const line = unlinked ? batchTargetUnlinkedLine(site.name) : failed.length > 0 ? failed[0] : RECOMMEND_NONE;
      patch({ phase: failed.length > 0 && !unlinked ? 'error' : 'none', items: [], line, error: failed.join(' ') });
      await say(line);
      return;
    }
    const line = recommendSpokenLine(items, site.name, ask.source);
    patch({ phase: 'asking', items, line, writeNote, error: failed.join(' ') });
    turnRef.current.setRecommend(channel);
    await say(line);
    await listen(channel);
  }, [args, patch, set, say, turnRef, listen]);

  const begin = useCallback(async (ask: RecommendAsk, channel: BatchChannel) => {
    const base: RecommendView = { phase: 'loading', channel, source: ask.source, scope: ask.scope, sites: [], site: null, items: [], line: 'Finding your connected sites…', writeNote: '', error: '' };
    if (!allowed()) {
      turnRef.current.clearSubStateIfKind('recommend');
      set({ ...base, phase: 'forbidden', line: RECOMMEND_OWNER_OPERATOR_ONLY });
      await say(RECOMMEND_OWNER_OPERATOR_ONLY);
      return;
    }
    set(base);
    let sites: BatchSiteOption[];
    try { sites = await args.batchDeps.listSites(); } catch (e) {
      patch({ phase: 'error', error: errText(e), line: BATCH_NO_SITE });
      await say(BATCH_NO_SITE);
      return;
    }
    const res = resolveBatchSite(sites, null, args.activeDomain ? args.activeDomain() : null);
    if (res.kind === 'none' || res.kind === 'out_of_domain') { patch({ phase: 'none', line: BATCH_NO_SITE }); await say(BATCH_NO_SITE); return; }
    if (res.kind === 'choose') { patch({ phase: 'choose_site', sites: res.options, line: BATCH_PICK_SITE }); await say(BATCH_PICK_SITE); return; }
    await load(res.site, ask, channel);
  }, [allowed, args, set, patch, say, load, turnRef]);

  const chooseSite = useCallback(async (siteId: number) => {
    const v = viewRef.current;
    const site = v?.sites.find((s) => s.id === siteId);
    if (!v || !site) return;
    await load(site, { scope: v.scope, source: v.source }, v.channel === 'voice' ? 'voice' : v.channel);
  }, [load]);

  /** Hand the selection to the existing flows (publish read-back first, then writes). */
  const execute = useCallback(async (selected: RecommendItem[], channel: BatchChannel) => {
    const v = viewRef.current;
    if (!v || !v.site || selected.length === 0) return;
    if (!allowed()) { await say(RECOMMEND_OWNER_OPERATOR_ONLY); return; }
    turnRef.current.clearSubStateIfKind('recommend');
    const site = v.site;
    const pubs = selected.filter((i) => i.kind === 'publish' && typeof i.draftId === 'number');
    const wrs = selected.filter((i) => i.kind === 'write' && typeof i.index === 'number');
    patch({ phase: 'handed_off', line: 'Picked ' + selected.map((i) => i.n).join(', ') + '.' });
    const sugg = suggestionRef.current;
    const writeRun = wrs.length > 0 && sugg ? () => args.suggest.adoptAndWrite(sugg, site, wrs.map((i) => i.index as number), channel) : null;
    if (pubs.length > 0) {
      await args.batch.presentDrafts(site, pubs.map((i) => i.draftId as number), channel, { mode: 'publish', afterConfirm: writeRun ?? undefined });
      return;
    }
    if (writeRun) await writeRun();
  }, [allowed, args, patch, say, turnRef]);

  const handleAnswer = useCallback(async (answer: string, channel: BatchChannel) => {
    const ctx = turnRef.current.subState;
    const v = viewRef.current;
    if (!ctx || ctx.kind !== 'recommend' || !v) return;
    const sel = parseListSelection(answer, v.items);
    if (sel.kind === 'no') {
      turnRef.current.clearSubStateIfKind('recommend');
      patch({ phase: 'cancelled', line: RECOMMEND_CANCEL_ACK });
      await say(RECOMMEND_CANCEL_ACK);
      return;
    }
    if (sel.kind === 'all') { await execute(v.items, channel); return; }
    if (sel.kind === 'some') { await execute(v.items.filter((i) => sel.ns.includes(i.n)), channel); return; }
    // ambiguous / bare yes / nothing recognised: ONE clarifier, then a second miss cancels.
    if (ctx.reAskCount >= RECOMMEND_MAX_REASKS + (sel.kind === 'ambiguous' ? 1 : 0)) {
      turnRef.current.clearSubStateIfKind('recommend');
      patch({ phase: 'cancelled', line: RECOMMEND_CANCEL_ACK });
      await say(RECOMMEND_CANCEL_ACK);
      return;
    }
    turnRef.current.incReask();
    let line = RECOMMEND_WHICH;
    if (sel.kind === 'ambiguous') {
      const c = sel.candidates.map((n) => v.items.find((i) => i.n === n)).filter((i): i is RecommendItem => Boolean(i));
      if (c.length >= 2) line = clarifyLine(c);
    }
    patch({ line });
    await say(line);
    await listen(channel);
  }, [execute, patch, say, turnRef, listen]);

  const beginWrite = useCallback(async (ask: SuggestWriteAsk, channel: BatchChannel) => {
    if (ask.queued) {
      // "write the blog … that is on the queue": already written -> offer to publish it.
      await args.batch.begin({ count: 1, siteHint: ask.siteHint, namedMode: 'publish', titleHint: ask.queued.titleHint, alreadyWritten: true }, channel);
      return;
    }
    if (ask.single && titleLike(ask.single.keyword) && allowed()) {
      // "write <title of a ready draft>": the draft exists -> say so and offer to publish.
      try {
        const sites = await args.batchDeps.listSites();
        const res = resolveBatchSite(sites, ask.siteHint, args.activeDomain ? args.activeDomain() : null);
        if (res.kind === 'one') {
          const c = await args.batchDeps.candidates(res.site.id, BATCH_MAX_COUNT);
          const hit = matchDraftTitle(ask.single.keyword, c.items ?? []);
          if (hit && hit.score >= 0.75) {
            await args.batch.begin({ count: 1, siteHint: ask.siteHint, namedMode: 'publish', titleHint: hit.item.title, alreadyWritten: true }, channel);
            return;
          }
        }
      } catch (e) { void e; /* not a queued draft we can see: the normal write flow below decides */ }
    }
    await args.suggest.begin(ask, channel);
  }, [args, allowed]);

  const pick = useCallback(async (n: number) => {
    const v = viewRef.current;
    const it = v?.items.find((i) => i.n === n);
    if (it) await execute([it], 'tap');
  }, [execute]);
  const pickAll = useCallback(async () => { const v = viewRef.current; if (v) await execute(v.items, 'tap'); }, [execute]);
  const dismiss = useCallback(() => { turnRef.current.clearSubStateIfKind('recommend'); suggestionRef.current = null; set(null); }, [set, turnRef]);

  return { view, begin, handleAnswer, beginWrite, pick, pickAll, chooseSite, dismiss };
}
